/* eslint-disable unicorn/consistent-function-scoping */
import { Button, colors, Icon, Input, Tooltip } from '@dev-spendesk/grapes';
import React, { useMemo, useState } from 'react';

import { useModal } from 'common/hooks/useModalGrapes';
import { useTranslation } from 'common/hooks/useTranslation';
import { NotificationType, useNotifications } from 'modules/app/notifications';
import { useDebouncedState } from 'src/core/common/hooks/useDebounceState';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { useGetSyncOperationStatus } from 'src/core/modules/accounting-integration/apis/useGetSyncOperation';
import { AnalyticalFieldMappingModalContainer } from 'src/core/modules/accounting-integration/components/AnalyticalFieldMapping/AnalyticalFieldMappingModalContainer';
import { CurrentMapping } from 'src/core/modules/accounting-integration/components/CurrentMapping';
import { useAnalyticalFieldMapping } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';
import { type AnalyticalFieldValueMappingV2 } from 'src/core/modules/bookkeep';
import { ReviewSyncSettingsWarning } from 'src/core/modules/company/components/ReviewSyncSettingsWarning';

import { CustomFieldEditValuesModalContainer } from '../../containers/CustomFieldEditValuesModalContainer';
import { CustomFieldLimitlessListContainer } from '../../containers/CustomFieldLimitlessListContainer';
import { useCustomFieldValuesQuery } from '../../containers/CustomFieldValuesListContainer/hooks';
import { type CustomField } from '../../models/customField';
import { type Team } from '../../models/team';
import { CustomFieldPanelHeader } from '../CustomFieldPanelHeader/CustomFieldPanelHeader';
import {
  CreateCustomFieldValueModal,
  DeleteCustomFieldValueModal,
  UpdateCustomFieldValueModal,
} from '../CustomFieldValueModals';
import { TeamsSelection } from '../fields/TeamsSelection';

type Props = {
  customField: CustomField;
  teams: Team[];
  selectedValueIds: string[];
  onUpdateCustomFieldValue: ({
    value,
    fieldValueId,
  }: {
    value: string;
    fieldValueId: string;
  }) => void;
  onCreateCustomFieldValue: ({
    value,
    customFieldId,
  }: {
    value: string;
    customFieldId: string;
  }) => void;
  onDeleteCustomFieldValue: ({
    fieldValueId,
  }: {
    fieldValueId: string;
  }) => void;
  onSelectedValuesChange: (
    selectedValueIds: string[],
    customFieldId: string,
  ) => void;
};

export const CustomFieldValuesList = ({
  customField,
  teams,
  selectedValueIds,
  onUpdateCustomFieldValue,
  onCreateCustomFieldValue,
  onDeleteCustomFieldValue,
  onSelectedValuesChange,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const { pushNotif } = useNotifications();
  const [searchInput, setSearchInput] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useDebouncedState('', 300);

  const { syncOperation } = useGetSyncOperationStatus('pull', customField.id);

  const customFieldValuesQueryState = useCustomFieldValuesQuery(
    customField.id,
    { search: debouncedSearch || undefined },
  );
  const customFieldValues =
    customFieldValuesQueryState.status === 'success'
      ? customFieldValuesQueryState.data
      : [];

  const customFieldValueNames =
    customFieldValues?.map(({ name }) => name) ?? [];

  const [valueMapping, setValueMapping] =
    useState<AnalyticalFieldValueMappingV2>();

  const mapping = useAnalyticalFieldMapping({
    fieldType: 'customField',
    field: customField,
    values: customFieldValues,
    onUpdated: () => {
      hideSyncSettingsModal();
    },
    onDeleteValue: (value) => {
      showDeleteCustomFieldValueModal({
        customFieldValueId: value.id,
        customFieldValueName: value.name,
      });
    },
  });

  const valueInDatabase = useMemo(
    () =>
      mapping.fieldMapping?.values.find(
        (value) => value.internalId === customField.id,
      ),
    [mapping.fieldMapping, customField],
  );

  const [
    updateCustomFieldValueModal,
    showUpdateCustomFieldValueModal,
    hideUpdateCustomFieldValueModal,
  ] = useModal<{ customFieldValueId: string; customFieldValueName: string }>(
    ({ isOpen, customFieldValueId, customFieldValueName }) => {
      const handleUpdateCustomFieldValue = async (createdValue: {
        value: string;
        fieldValueId: string;
      }) => {
        try {
          await onUpdateCustomFieldValue(createdValue);

          if (mapping.isMapSyncEnabled && mapping.fieldMapping?.internalId) {
            mapping.handleValueMapping({
              value: valueMapping,
              internalId: customFieldValueId,
            });

            setValueMapping(undefined);
          }

          pushNotif({
            type: NotificationType.Success,
            message: t('customFields.settings.modals.edition.success'),
          });
        } catch {
          pushNotif({
            type: NotificationType.Danger,
            message: t('customFields.settings.modals.edition.error'),
          });
        }
      };

      return (
        <UpdateCustomFieldValueModal
          isOpen={isOpen}
          customFieldValues={customFieldValueNames}
          valueId={customFieldValueId}
          value={customFieldValueName}
          onClose={() => {
            setValueMapping(undefined);
            hideUpdateCustomFieldValueModal();
          }}
          onSubmit={(value: string) => {
            handleUpdateCustomFieldValue({
              value,
              fieldValueId: customFieldValueId,
            });
          }}
          fieldMapping={mapping.fieldMapping}
          valueMapping={valueMapping}
          setValueMapping={setValueMapping}
          isMapSyncEnabled={mapping.isMapSyncEnabled}
          syncMode={mapping.syncMode}
          valueInDatabase={valueInDatabase}
        />
      );
    },
  );

  const [createCustomFieldValueModal, , hideCreateCustomFieldValueModal] =
    useModal<{ customFieldValueId: string }>(({ isOpen }) => {
      const handleCreateCustomFieldValue = async (updatedValue: {
        value: string;
        customFieldId: string;
      }) => {
        try {
          await onCreateCustomFieldValue(updatedValue);
          pushNotif({
            type: NotificationType.Success,
            message: t('customFields.settings.modals.creation.success'),
          });
          hideCreateCustomFieldValueModal();
        } catch {
          pushNotif({
            type: NotificationType.Danger,
            message: t('customFields.settings.modals.creation.error'),
          });
        }
      };

      return (
        <CreateCustomFieldValueModal
          isOpen={isOpen}
          customFieldValues={customFieldValueNames}
          onClose={() => hideCreateCustomFieldValueModal()}
          onSubmit={(value: string) => {
            handleCreateCustomFieldValue({
              value,
              customFieldId: customField.id,
            });
          }}
        />
      );
    });

  const [
    deleteCustomFieldValueModal,
    showDeleteCustomFieldValueModal,
    hideDeleteCustomFieldValueModal,
  ] = useModal<{ customFieldValueId: string; customFieldValueName: string }>(
    ({ isOpen, customFieldValueId, customFieldValueName }) => {
      const handleDeleteCustomFieldValue = async (deletedValue: {
        fieldValueId: string;
      }) => {
        try {
          await onDeleteCustomFieldValue(deletedValue);
          pushNotif({
            type: NotificationType.Success,
            message: t('customFields.settings.modals.deletion.success'),
          });

          if (mapping.isMapSyncEnabled && mapping.fieldMapping?.internalId) {
            mapping.onSaveChanges({
              mapping: {
                fieldId: mapping.fieldMapping.id,
                fieldType: mapping.fieldMapping.fieldType,
                internalId: mapping.fieldMapping.internalId,
                values: mapping.fieldMapping.values.map((value) => {
                  if (value.internalId === deletedValue.fieldValueId) {
                    return {
                      ...value,
                      internalId: null,
                    };
                  }
                  return value;
                }),
              },
            });
          }

          hideDeleteCustomFieldValueModal();
        } catch {
          pushNotif({
            type: NotificationType.Danger,
            message: t('customFields.settings.modals.deletion.error'),
          });
        }
      };

      const isValueMapped =
        mapping.syncMode === 'pullAndMap' &&
        mapping.fieldMapping?.values.some(
          (value) => value.internalId === customFieldValueId,
        );

      return (
        <DeleteCustomFieldValueModal
          isOpen={isOpen}
          onClose={() => hideDeleteCustomFieldValueModal()}
          value={customFieldValueName}
          onSubmit={() => {
            handleDeleteCustomFieldValue({
              fieldValueId: customFieldValueId,
            });
          }}
          isValueMapped={isValueMapped}
        />
      );
    },
  );

  const [customFieldEditValuesModal, showCustomFieldEditValuesModal] = useModal(
    ({ onClose, isOpen }) => {
      return (
        <CustomFieldEditValuesModalContainer
          isOpen={isOpen}
          customField={customField}
          onClose={() => onClose()}
          fieldMapping={mapping.fieldMapping}
          handleValueMapping={mapping.handleValueMapping}
          isMapSyncEnabled={mapping.isMapSyncEnabled}
          syncMode={mapping.syncMode}
        />
      );
    },
  );

  const [syncSettingsModal, showSyncSettingsModal, hideSyncSettingsModal] =
    useModal(({ isOpen }) => (
      <AnalyticalFieldMappingModalContainer
        isOpen={isOpen}
        onClose={() => hideSyncSettingsModal()}
        isSplittable={!!customField.isSplittable}
        internalField={customField}
        values={customFieldValues}
        fieldType="customField"
        mapping={mapping}
      />
    ));

  return (
    <>
      <div className="mb-32">
        <CustomFieldPanelHeader
          customField={customField}
          onSyncSettingsClick={
            mapping.isMapSyncEnabled ? showSyncSettingsModal : undefined
          }
        >
          {customField.type === 'list' && (
            <Input
              type="search"
              className="w-full"
              placeholder={t('misc.search')}
              value={searchInput}
              fit="content"
              leftAddon={
                <Icon
                  name="magnifying-glass"
                  color={colors.contentDecorativeIcon}
                  className="ml-8"
                  size="m"
                />
              }
              onChange={(event) => {
                const value = event.target.value;
                setSearchInput(value);
                setDebouncedSearch(value);
              }}
            />
          )}

          {mapping.isMapSyncEnabled && mapping.syncMode === 'pull' ? (
            <Tooltip
              content={t(
                'expenseCategories.mapSync.addExpenseCategoryDisabledTooltip',
                { integration: accountingSoftwareName },
              )}
            >
              <Button
                text={t('customFields.settings.headerButtonValues')}
                variant="secondaryNeutral"
                className="shrink-0"
                isDisabled={
                  mapping.isMapSyncEnabled && mapping.syncMode === 'pull'
                }
                onClick={() => showCustomFieldEditValuesModal()}
              />
            </Tooltip>
          ) : (
            <Button
              text={t('customFields.settings.headerButtonValues')}
              variant="secondaryNeutral"
              className="shrink-0"
              isDisabled={
                mapping.isMapSyncEnabled && mapping.syncMode === 'pull'
              }
              onClick={() => showCustomFieldEditValuesModal()}
            />
          )}
        </CustomFieldPanelHeader>
        {mapping.showWarningCallout && (
          <ReviewSyncSettingsWarning onClick={() => showSyncSettingsModal()} />
        )}

        {mapping.isMapSyncEnabled && !mapping.isFieldMappingsLoading && (
          <CurrentMapping
            fieldName={mapping.fieldMapping?.externalName}
            isLoading={mapping.isIntegrationNameLoading}
            isWrapped
            syncMode={mapping.syncMode}
          />
        )}

        <CustomFieldLimitlessListContainer
          customFieldValuesQueryState={customFieldValuesQueryState}
          onSelectedValuesChange={(selectedValues) => {
            onSelectedValuesChange(selectedValues, customField.id);
          }}
          selectedValueIds={selectedValueIds}
          fieldMapping={mapping.fieldMapping}
          showDeleteCustomFieldValueModal={showDeleteCustomFieldValueModal}
          showUpdateCustomFieldValueModal={showUpdateCustomFieldValueModal}
          isSearching={Boolean(debouncedSearch)}
          searchTerm={debouncedSearch}
          syncMode={mapping.syncMode}
          isMapSyncEnabled={mapping.isMapSyncEnabled}
          syncOperation={syncOperation}
        />
      </div>
      {customField.isScopable && (
        <TeamsSelection
          cfId={customField.id}
          groups={teams ?? []}
          isAllScopes={customField.isAllScopes}
        />
      )}
      {updateCustomFieldValueModal}
      {createCustomFieldValueModal}
      {deleteCustomFieldValueModal}
      {customFieldEditValuesModal}
      {syncSettingsModal}
    </>
  );
};
